// ERP reference data for the invoice review dropdowns (deal names, account
// assignments, analytic parts, units of measure). All Bulgarian, mirrors the
// shapes consumed by src/screens/SaldoReview/.../hooks/useErpOptions.ts and
// LineItemErpRow.tsx. action_type: 103 = purchases, 102 = sales.

export interface DealName {
  id: number;
  vat_rate: number;
  transaction_type: string;
  description: string;
  vat_code?: string;
}

export interface AccAssignment {
  id: number;
  description: string;
  action_type: number;
  code: string;
  is_active: boolean;
}

export interface UnitOfMeasure {
  code: string;
  name: string;
  print?: string;
}

interface AnalyticPartOption {
  value: string;
  label: string;
  apx_id?: number;
}

interface AnalyticPartFieldDef {
  field_id: number;
  field_name: string;
  label: string;
  row_num: number;
  lookup_nomen_type_fk: number;
  options: AnalyticPartOption[];
  all_options: AnalyticPartOption[];
}

interface AnalyticPartDefinition {
  account_id: number;
  account_code: string;
  account_name: string;
  analytic_part_id: number;
  acc_obj_id: number;
  directions: string[];
  lookup_nomen_type_fk: number;
  options: AnalyticPartOption[];
  fields: AnalyticPartFieldDef[];
  combinations: Array<{ apx_id: number; levels: Array<{ row_num: number; code: string; name: string }> }>;
}

// Purchases (action_type 103)
const PURCHASE_DEAL_NAMES: DealName[] = [
  { id: 1, vat_rate: 20, transaction_type: 'purchase', description: 'Покупка на стоки и услуги в страната', vat_code: '1' },
  { id: 2, vat_rate: 20, transaction_type: 'purchase', description: 'Покупка на ДМА в страната', vat_code: '1' },
  { id: 3, vat_rate: 0, transaction_type: 'purchase', description: 'Необлагаема доставка / без ДДС', vat_code: '0' },
  { id: 4, vat_rate: 0, transaction_type: 'purchase', description: 'ВОП / обратно начисляване (чл. 82)', vat_code: '2' },
];

// Sales (action_type 102) — kept minimal; demo invoices are all purchases.
const SALES_DEAL_NAMES: DealName[] = [
  { id: 101, vat_rate: 20, transaction_type: 'sales', description: 'Продажба на стоки и услуги в страната', vat_code: '1' },
  { id: 102, vat_rate: 0, transaction_type: 'sales', description: 'ВОД / износ (0% ДДС)', vat_code: '0' },
];

const PURCHASE_ACC_ASSIGNMENTS: AccAssignment[] = [
  { id: 1, description: 'Разход за външни услуги 602/401', action_type: 103, code: '602', is_active: true },
  { id: 2, description: 'ДМА 204/401', action_type: 103, code: '204', is_active: true },
  { id: 3, description: 'Материали 302/401', action_type: 103, code: '302', is_active: true },
  { id: 4, description: 'Разход за бъдещи периоди 650/401', action_type: 103, code: '650', is_active: true },
  { id: 5, description: 'Разход за материали 601/401', action_type: 103, code: '601', is_active: true },
  { id: 6, description: 'Други разходи 609/401', action_type: 103, code: '609', is_active: true },
];

const SALES_ACC_ASSIGNMENTS: AccAssignment[] = [
  { id: 51, description: 'Приход от продажби 411/702', action_type: 102, code: '702', is_active: true },
];

const UNITS_OF_MEASURE: UnitOfMeasure[] = [
  { code: 'бр.', name: 'брой', print: 'бр.' },
  { code: 'кг', name: 'килограм', print: 'кг' },
  { code: 'л', name: 'литър', print: 'л' },
  { code: 'усл.', name: 'услуга', print: 'усл.' },
  { code: 'м', name: 'метър', print: 'м' },
];

// account_code by acc_assignment id — analytic_parts entries key on account_code.
const ACC_CODE_BY_ASSIGNMENT: Record<number, { code: string; name: string }> = {
  1: { code: '602', name: 'Разходи за външни услуги' },
  2: { code: '204', name: 'Машини, съоръжения и оборудване' },
  3: { code: '302', name: 'Материали' },
  4: { code: '650', name: 'Разходи за бъдещи периоди' },
  5: { code: '601', name: 'Разходи за материали' },
  6: { code: '609', name: 'Други разходи' },
  51: { code: '702', name: 'Приходи от продажби' },
};

const OBEKT_OPTIONS: AnalyticPartOption[] = [
  { value: '01', label: 'Централен склад', apx_id: 101 },
  { value: '02', label: 'Офис София', apx_id: 102 },
  { value: '03', label: 'Обект Пловдив', apx_id: 103 },
];

const PARTIDA_OPTIONS: AnalyticPartOption[] = [
  { value: 'Д-2026-01', label: 'Договор 2026-01', apx_id: 201 },
  { value: 'Д-2026-02', label: 'Договор 2026-02', apx_id: 202 },
];

export function getDealNames(actionType: number): DealName[] {
  return actionType === 102 ? SALES_DEAL_NAMES : PURCHASE_DEAL_NAMES;
}

export function getAccAssignments(actionType: number): AccAssignment[] {
  return actionType === 102 ? SALES_ACC_ASSIGNMENTS : PURCHASE_ACC_ASSIGNMENTS;
}

export function getUnitsOfMeasure(): UnitOfMeasure[] {
  return UNITS_OF_MEASURE;
}

// One definition per account: a lookup field ("Обект") plus a free-text-style
// field ("Партида"). No combinations → single-level, no cascading validation.
export function getAnalyticParts(accAssignmentId: number): AnalyticPartDefinition[] {
  const acc = ACC_CODE_BY_ASSIGNMENT[accAssignmentId];
  if (!acc) return [];

  const fields: AnalyticPartFieldDef[] = [
    {
      field_id: 1,
      field_name: 'obekt',
      label: 'Обект',
      row_num: 1,
      lookup_nomen_type_fk: 5,
      options: OBEKT_OPTIONS,
      all_options: OBEKT_OPTIONS,
    },
    {
      field_id: 2,
      field_name: 'partida',
      label: 'Партида',
      row_num: 2,
      lookup_nomen_type_fk: 0,
      options: PARTIDA_OPTIONS,
      all_options: PARTIDA_OPTIONS,
    },
  ];

  return [
    {
      account_id: accAssignmentId,
      account_code: acc.code,
      account_name: acc.name,
      analytic_part_id: accAssignmentId * 10,
      acc_obj_id: accAssignmentId * 100,
      directions: ['Дебит'],
      lookup_nomen_type_fk: 5,
      options: OBEKT_OPTIONS,
      fields,
      combinations: [],
    },
  ];
}
